// src/scripts/modules/publications.js
import { module } from 'modujs';

export default class extends module {
  constructor(m) {
    super(m);
    this.url = new URL(window.location.href);
    this.ajaxURL = (window.ajaxVars && window.ajaxVars.url) || '/wp-admin/admin-ajax.php';

    this._autosizeAll = this._autosizeAll.bind(this);
    this.onChangeFilter = this.onChangeFilter.bind(this);
    this.onClickMore = this.onClickMore.bind(this);
    this.updateResetVisibility = this.updateResetVisibility.bind(this);
    this.onReset = this.onReset.bind(this);
  }

  /* ----------------- INIT ----------------- */
  init() {
    // Liste dans la section publications
    this.container = this.el.querySelector('.ajaxList');
    this.results = this.el.querySelector('.ajaxList_results');
    this.bindLoadMore();

    // Barre de filtres (hors du module)
    this.filtersRoot = document.querySelector('.publicationsFilters');
    this.resetBtn = this.filtersRoot?.querySelector('.publicationsFilters_reset');

    // Tous les <select>
    this.selects = document.querySelectorAll('.publicationsFilters .js-filter[data-param]');

    // Amélioration UI + événements
    this.selects.forEach((sel) => {
      this.enhanceSelect(sel);        // crée le faux dropdown
      this._sizeSelect(sel);          // width auto
      this._toggleFilled(sel);        // opacité + état
      sel.addEventListener('change', this.onChangeFilter);
      sel.addEventListener('input', this.onChangeFilter);
    });

    // Reset button
    if (this.resetBtn) this.resetBtn.addEventListener('click', this.onReset);
    this.updateResetVisibility();

    // Fermeture des menus fake
    this._onDocClick = (e) => {
      document.querySelectorAll('.publicationsFilters_select.--open')
        .forEach((w) => { if (!w.contains(e.target)) w.classList.remove('--open'); });
    };
    this._onKeyDown = (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.publicationsFilters_select.--open')
          .forEach((w) => w.classList.remove('--open'));
      }
    };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeyDown);

    // Resize widths
    window.addEventListener('resize', this._autosizeAll, { passive: true });
  }

  /* ----------------- Fake dropdown ----------------- */
  enhanceSelect(sel) {
    const wrap = sel.closest('.publicationsFilters_select');
    if (!wrap) return;

    // Vérifier si déjà amélioré pour éviter les doublons
    if (sel._enhanced) return;

    // Vérifier si les éléments fake existent déjà
    if (wrap.querySelector('.pfake') || wrap.querySelector('.pfake_menu')) {
      sel._enhanced = true;
      return;
    }

    // cache visuel (garde l'accessibilité et les events)
    sel.classList.add('is-visually-hidden');

    // bouton visible
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pfake';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = sel.options[sel.selectedIndex]?.text || sel.options[0]?.text || '';

    // menu
    const list = document.createElement('ul');
    list.className = 'pfake_menu';
    list.setAttribute('role', 'listbox');

    Array.from(sel.options).forEach((opt) => {
      if (opt.value === '') return;

      const li = document.createElement('li');
      li.className = 'pfake_option';
      li.setAttribute('role', 'option');
      li.dataset.value = opt.value;
      li.textContent = opt.text;

      if (opt.selected) li.classList.add('is-active');
      list.appendChild(li);
    });

    wrap.appendChild(btn);
    wrap.appendChild(list);

    // Marquer comme amélioré pour éviter les doublons
    sel._enhanced = true;
    sel._fakeBtn = btn;
    sel._fakeList = list;

    // open/close
    btn.addEventListener('click', () => {
      const open = wrap.classList.toggle('--open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // choix
    list.addEventListener('click', (e) => {
      const li = e.target.closest('.pfake_option');
      if (!li) return;

      // MAJ select natif + event standard (ton flux AJAX)
      sel.value = li.dataset.value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));

      // MAJ UI
      list.querySelectorAll('.is-active').forEach((n) => n.classList.remove('is-active'));
      li.classList.add('is-active');
      btn.textContent = li.textContent;

      wrap.classList.remove('--open');
      btn.setAttribute('aria-expanded', 'false');

      // recalc width
      this._sizeSelect(sel);
    });

    // nav clavier simple
    btn.addEventListener('keydown', (e) => {
      const items = Array.from(list.querySelectorAll('.pfake_option'));
      const idx = items.findIndex((li) => li.classList.contains('is-active'));
      if (e.key === 'ArrowDown') { e.preventDefault(); (items[idx + 1] || items[0]).click(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); (items[idx - 1] || items[items.length - 1]).click(); }
      if (e.key === 'Enter' && wrap.classList.contains('--open')) { e.preventDefault(); items[idx]?.click(); }
    });

    sel._enhanced = true;
    sel._fakeBtn = btn;
    sel._fakeList = list;
  }

  _autosizeAll() { this.selects.forEach((s) => this._sizeSelect(s)); }

  _toggleFilled(sel) {
    const wrap = sel.closest('.publicationsFilters_select');
    if (!wrap) return;
    wrap.classList.toggle('--filled', !!sel.value);
  }

  _sizeSelect(sel) {
    const wrap = sel.closest('.publicationsFilters_select');
    if (!wrap) return;

    if (!sel._mirror) {
      const span = document.createElement('span');
      sel._mirror = span;
      document.body.appendChild(span);
      const cs = getComputedStyle(wrap); // on prend la typo effective
      span.style.cssText = `
        position:absolute; left:-9999px; top:-9999px; visibility:hidden;
        white-space:pre; pointer-events:none;
        font: ${cs.fontWeight} ${cs.fontSize}/${cs.lineHeight} ${cs.fontFamily};
        letter-spacing:${cs.letterSpacing};
      `;
    }
    sel._mirror.textContent = sel.options[sel.selectedIndex]?.text || '';
    const extra = 24 + 22; // padding + flèche
    const w = Math.ceil(sel._mirror.getBoundingClientRect().width + extra);
    wrap.style.setProperty('--w', `${w}px`);
  }

  /* ----------------- Events filtres ----------------- */
  onChangeFilter(e) {
    const sel = e.currentTarget;
    this._sizeSelect(sel);
    this._toggleFilled(sel);

    // MAJ URL à partir de tous les filtres
    this.selects.forEach(s => {
      const key = s.dataset.param;
      const val = s.value;
      if (val) this.url.searchParams.set(key, val);
      else this.url.searchParams.delete(key);
    });

    // reset pagination
    this.url.searchParams.delete('pg');
    this.url.searchParams.delete('paged');

    history.pushState(null, '', this.url.toString());
    this.updateResetVisibility();
    this.fetchPublications(false);
  }

  /* ----------------- Load more ----------------- */
  bindLoadMore() {
    const btn = this.el.querySelector('.loadMoreButton');
    if (btn) btn.addEventListener('click', this.onClickMore, { once: true });
  }

  onClickMore(e) {
    e.preventDefault();
    const currentPg = Number(this.url.searchParams.get('pg') || 1);
    this.url.searchParams.set('pg', String(currentPg + 1));
    history.pushState(null, '', this.url.toString());
    this.fetchPublications(true);
  }

  /* ----------------- AJAX ----------------- */
  fetchPublications(append = false) {
    if (!this.container) return;

    this.container.classList.add('ajaxList--loading');

    const qs = new URLSearchParams();
    qs.set('action', 'publications_ajax');

    // filtres
    const format = this.url.searchParams.get('format');
    const thematique = this.url.searchParams.get('thematique');
    if (format) qs.set('format', format);
    if (thematique) qs.set('thematique', thematique);

    // pagination
    const pg = Number(this.url.searchParams.get('pg') || 1);
    qs.set('pg', String(pg));
    if (append) qs.set('paged', String(pg));
    else {
      const urlPaged = this.url.searchParams.get('paged');
      if (urlPaged) qs.set('paged', urlPaged);
    }

    return fetch(`${this.ajaxURL}?${qs.toString()}`, { credentials: 'same-origin' })
      .then(r => r.text())
      .then(html => {
        this.container.classList.remove('ajaxList--loading');

        if (append) {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;

          const newList = tmp.querySelector('.publications_list');
          const newRow = newList?.querySelector('.articles_row');
          const newMore = newList?.querySelector('.loadMoreWrap');
          const curList = this.results.querySelector('.publications_list');
          const curRow = curList.querySelector('.articles_row');

          curList.querySelector('.loadMoreWrap')?.remove();

          if (newRow) {
            Array.from(newRow.children).forEach(node => curRow.appendChild(node));
          }
          if (newMore) curList.appendChild(newMore);

        } else {
          this.results.innerHTML = html;
        }

        this.bindLoadMore();
        if (window.scroll && typeof window.scroll.update === 'function') window.scroll.update();
      })

      .catch(err => {
        this.container.classList.remove('ajaxList--loading');
        console.error('Publications AJAX error:', err);
      });
  }

  /* ----------------- Reset ----------------- */
  updateResetVisibility() {
    const hasValue = Array.from(this.selects).some(s => !!s.value);
    if (!this.filtersRoot) return;
    this.filtersRoot.classList.toggle('publicationsFilters--active', hasValue);
    if (this.resetBtn) this.resetBtn.disabled = !hasValue;
  }

  onReset() {
    this.selects.forEach(sel => {
      sel.value = '';
      // MAJ faux bouton + item actif
      if (sel._fakeBtn && sel.options[0]) sel._fakeBtn.textContent = sel.options[0].text;
      if (sel._fakeList) {
        sel._fakeList.querySelectorAll('.is-active').forEach(n => n.classList.remove('is-active'));
        // Pas d'option active quand on est sur value=""
      }
      this._sizeSelect(sel);
      this._toggleFilled(sel);
    });

    this.url.searchParams.delete('format');
    this.url.searchParams.delete('thematique');
    this.url.searchParams.delete('pg');
    this.url.searchParams.delete('paged');
    history.pushState(null, '', this.url.toString());

    this.updateResetVisibility();
    this.fetchPublications(false);
  }

  /* ----------------- Destroy ----------------- */
  destroy() {
    this.selects?.forEach(sel => {
      sel.removeEventListener('change', this.onChangeFilter);
      sel.removeEventListener('input', this.onChangeFilter);
    });
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('resize', this._autosizeAll);
    const btn = this.el.querySelector('.loadMoreButton');
    if (btn) btn.removeEventListener('click', this.onClickMore);
    if (this.resetBtn) this.resetBtn.removeEventListener('click', this.onReset);
  }
}
